import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Select, { components } from "react-select";

interface Option {
  label: string;
  value: string;
}

interface DropdownMultiOptionProps {
  name?: string;
  initialValue?: Option | Option[] | undefined | null;
  dataList?: Option[];
  onChangeSelect?: (params: Option | Option[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  classNamePrefix?: string;
  isHasOptionAll?: boolean;
  isHaveMoreInfo?: boolean;
  optionAll?: Option;
  labelTop?: string;
  isMulti?: boolean;
  cacheOptions?: boolean;
  style?: any;
}


const CheckboxOption = (props: any) => {
  const { isSelected, innerRef, innerProps, data, selectProps } = props;
  const { value: selectedValues = [], options = [] } = selectProps;

  if (data.value === "__ALL__") {
    const normalOptions = options.filter((opt: any) => opt.value !== "__ALL__");
    const selectedNormal = selectedValues.filter((v: any) => v.value !== "__ALL__");

    const allSelected = normalOptions.length > 0 && selectedNormal.length === normalOptions.length;

    return (
      <div
        ref={innerRef}
        {...innerProps}
        style={{
          display: "flex",
          alignItems: "center",
          padding: "8px 12px",
          backgroundColor: "#fff",
          cursor: "pointer",
          fontWeight: "bold",
          borderBottom: "1px solid #eee",
          position: "sticky",
          top: -5,
          zIndex: 1,
        }}
      >
        <input
          type="checkbox"
          checked={allSelected}
          onChange={() => { }}
          style={{ marginRight: 8 }}
          tabIndex={-1}
          readOnly
        />
        <span>{data.label}</span>
      </div>
    );
  }

  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        display: "flex",
        alignItems: "center",
        padding: "8px 12px",
        backgroundColor: props.isFocused ? "rgba(0,0,0,0.04)" : "transparent",
        cursor: "pointer",
      }}
    >
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => { }}
        style={{ marginRight: 8 }}
        tabIndex={-1}
        readOnly
      />
      <span>{data.label}</span>
    </div>
  );
};

const CustomValueContainer = ({ children, ...props }: any) => {

  const { getValue } = props;
  const selected = getValue();

  const selectedNormal = selected.filter((opt: Option) => opt.value !== "__ALL__");

  let displayText = selectedNormal.map((opt: Option) => opt.label).join(", ");

  if (!displayText) {
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
  }

  return (
    <components.ValueContainer {...props}>
      <div
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          flex: 1,
          minWidth: 0,
          maxWidth: '80%',
          paddingRight: 8,
        }}
        title={displayText}
      >
        {displayText}
      </div>
      {children[1]}
    </components.ValueContainer>
  );
};

const DropdownSelectMultiOption = ({
  name = "",
  isHaveMoreInfo = false,
  initialValue,
  onChangeSelect,
  dataList = [],
  placeholder,
  disabled = false,
  className = "",
  classNamePrefix = "",
  isHasOptionAll = false,
  optionAll = { label: "All", value: "__ALL__" },
  labelTop = "",
  isMulti = true,
  cacheOptions = false,
  style = {},
}: DropdownMultiOptionProps) => {
  const { t } = useTranslation();

  // === Options ===
  const normalOptions = useMemo(
    () =>
      dataList.map((item) => ({
        ...(isHaveMoreInfo ? item : {}),
        label: item.label,
        value: String(item.value),
      })),
    [dataList, isHaveMoreInfo]
  );

  const options = useMemo(() => {
    return isHasOptionAll ? [{ label: `${t(optionAll?.label)} (${normalOptions?.length})`, value: optionAll?.value }, ...normalOptions] : normalOptions;
  }, [isHasOptionAll, optionAll, normalOptions, t]);

  const [selected, setSelected] = useState<Option[]>([]);

  useEffect(() => {
    if (!initialValue) {
      setSelected([]);
      return;
    }
    const vals = Array.isArray(initialValue) ? initialValue : [initialValue];
    setSelected(vals.filter((v) => v.value !== "__ALL__"));
  }, [initialValue]);


  const handleChange = (newValue: any) => {
    if (!Array.isArray(newValue)) {
      setSelected([]);
      onChangeSelect?.([]);
      return;
    }

    const newOptions = newValue as Option[];
    const allWereSelected = selected.length === normalOptions.length && normalOptions.length > 0;
    const clickedAll = newOptions.some(opt => opt.value === "__ALL__");

    let result: Option[] = [];

    if (clickedAll) {
      if (!allWereSelected) {
        result = normalOptions.map(opt => ({ ...opt }));
      } else {
        result = newOptions.filter(opt => opt.value !== "__ALL__").map(opt => ({ ...opt }));
      }
    } else {
      if (allWereSelected) {
        result = [];
      } else {
        result = newOptions.filter(opt => opt.value !== "__ALL__").map(opt => ({ ...opt }));
      }
    }

    setSelected(result);
    onChangeSelect?.(result);
  };


  const derivedValue = useMemo(() => {
    const allSelected = selected.length === normalOptions.length && normalOptions.length > 0;

    if (allSelected) {
      return [optionAll, ...selected];
    }
    return selected;
  }, [selected, normalOptions, optionAll]);


  return (
    <div className="position-relative" style={style}>
      <Select
        options={options}
        value={derivedValue}
        name={name}
        isDisabled={disabled}
        cacheOptions={cacheOptions}
        onChange={handleChange}
        placeholder={placeholder || ""}
        loadingMessage={() => `${t("Loading")}...`}
        noOptionsMessage={() => t("No Options")}
        className={className}
        classNamePrefix={classNamePrefix}
        isClearable={false}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        isMulti={isMulti}
        components={{
          Option: CheckboxOption,
          ValueContainer: CustomValueContainer,
        }}
        styles={{
          valueContainer: (base: any) => ({
            ...base,
            display: "flex",
            alignItems: "center",
            padding: "2px 8px",
            overflow: "hidden",
            flexWrap: "nowrap",
          }),
          input: (base: any) => ({
            ...base,
            margin: 0,
            padding: 0,
          }),
          placeholder: (base: any) => ({
            ...base,
            color: "#999",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }),
        }}
      />
      {labelTop && selected.length > 0 && (
        <label
          style={{
            position: "absolute",
            left: "5px",
            top: "0px",
            transform: "translateY(-50%)",
            padding: "0 5px",
            color: "#c2c2c2",
            fontSize: "12px",
            fontWeight: 400,
            borderRadius: "3px",
            backgroundColor: disabled ? "hsl(0, 0%, 95%)" : "var(--vz-secondary-bg)",
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          {labelTop}
        </label>
      )}
    </div>
  );
};

export default React.memo(DropdownSelectMultiOption);